import { useRef, useState } from "react";
import { Eye, Download, Maximize2, FileText, FileSpreadsheet, FileType, Image as ImageIcon, File } from "lucide-react";
import FilePreviewDialog, { previewKind } from "@/components/common/FilePreviewDialog";
import QuickViewDialog from "@/components/common/QuickViewDialog";

/** Couleur de l'icône téléchargement selon l'extension (spec iter305). */
export function fileKindStyle(name) {
  const ext = String(name || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return { bg: "#3a1e1e", fg: "#f87171", label: "PDF" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { bg: "#1a3328", fg: "#22C55E", label: "Excel" };
  if (["doc", "docx"].includes(ext)) return { bg: "#1e2a3a", fg: "#3B82F6", label: "Word" };
  if (["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(ext)) return { bg: "#2a2438", fg: "#A855F7", label: "Image" };
  return { bg: "#1a1a1e", fg: "#8a8a8f", label: "Fichier" };
}
import UserAvatar from "@/components/ui/UserAvatar";
import { useUserDirectory } from "@/lib/userDirectory";
import api from "@/lib/api";
import { toast } from "@/lib/toast";
import { taskEventLabel } from "@/components/tasks/TaskHistoryDialog";

const pad = (n) => String(n).padStart(2, "0");
const fmt = (iso) => {
  const d = new Date(iso);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Infobulle générique « Activité » (spec iter300/301) — 2 lignes + 2 icônes. */
const FILE_ICON = (name) => {
  const ext = String(name || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return { Icon: FileText, color: "#d97a7a" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { Icon: FileSpreadsheet, color: "#7fbf94" };
  if (["doc", "docx"].includes(ext)) return { Icon: FileType, color: "#7fa8dd" };
  if (["jpg", "jpeg", "png", "webp", "gif", "heic"].includes(ext)) return { Icon: ImageIcon, color: "#a79ae0" };
  return { Icon: File, color: "#8b8b93" };
};

async function downloadAll(files) {
  for (const f of files) {
    try {
      const r = await api.get(f.url, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = f.name || "fichier";
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error("Téléchargement impossible", { description: f.name || "" });
    }
  }
}

/** Spec iter320 — lignes de la session de modification. */
function SessionEvents({ events, more, usersById, seed, testId }) {
  const [preview, setPreview] = useState(null);
  const { byId: dirById } = useUserDirectory();
  return (
    <div className="flex flex-col text-left" style={{ width: "max-content", maxWidth: 354 }} data-testid={`${testId}-events`}>
      {events.map((ev, i) => {
        const u = dirById[ev.actor_id] || usersById?.[ev.actor_id] || (ev.actor_name ? { id: ev.actor_id || seed, first_name: ev.actor_name.split(" ")[0], last_name: ev.actor_name.split(" ").slice(1).join(" ") } : null);
        const files = ev.files || [];
        const fi = FILE_ICON(files[0]?.name);
        const names = files.map((f) => f.name).join("\n");
        return (
          <div key={i}>
            {i > 0 && <div style={{ height: 1, background: "#2a2a30", margin: "10px 0" }} />}
            <div className="flex items-center" style={{ gap: 9 }} data-testid={`${testId}-event-${i}`}>
              <UserAvatar user={u} size={22} showPopover={false} className="shrink-0" />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="block" style={{ fontSize: 12.5, color: "#e8e8ec", lineHeight: 1.45, whiteSpace: "normal", wordBreak: "break-word" }}>{ev.label}</span>
                <span className="block tabular-nums" style={{ fontSize: 11, color: "#8b8b93", marginTop: 3 }}>{fmt(ev.created_at)}</span>
              </span>
              {files.length > 0 && (<>
                <span aria-hidden="true" style={{ width: 1, alignSelf: "stretch", background: "#2a2a30", margin: "0 2px 0 8px", flexShrink: 0 }} />
                <span className="flex items-center" style={{ gap: 9, flexShrink: 0 }}>
                  <button type="button" title={names} style={{ color: "#7fa8dd" }} className="hover:opacity-80"
                    onClick={(e) => { e.stopPropagation(); setPreview(files[0]); }} data-testid={`${testId}-event-${i}-view`}><Eye size={16} /></button>
                  <button type="button" title={names} style={{ color: fi.color }} className="hover:opacity-80"
                    onClick={(e) => { e.stopPropagation(); downloadAll(files); }} data-testid={`${testId}-event-${i}-download`}><fi.Icon size={16} /></button>
                </span>
              </>)}
            </div>
          </div>
        );
      })}
      {more > 0 && <div style={{ fontSize: 11, color: "#8b8b93", marginTop: 10 }} data-testid={`${testId}-events-more`}>+ {more} autre{more > 1 ? "s" : ""} modification{more > 1 ? "s" : ""}</div>}
      {preview && <FilePreviewDialog open onOpenChange={(o) => !o && setPreview(null)} url={preview.url} filename={preview.name} />}
    </div>
  );
}

export function ActivityTip({ iso, label, user, seed, quickView, downloadUrl, downloadName, testId, events = null, more = 0, usersById = null, children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [preview, setPreview] = useState(false);
  const [quick, setQuick] = useState(false);  // spec iter307 — aperçu rapide (modale, sans navigation)
  const timer = useRef(null);
  const { byId: dirById } = useUserDirectory();
  if (!iso) return children;
  const cancelClose = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const show = (e) => {
    cancelClose();
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ x: r.left + r.width / 2, y: r.bottom });
    setOpen(true);
  };
  // Fermeture différée + pont invisible (padding-top) : zone de survol continue pastille → infobulle.
  const hide = () => { cancelClose(); timer.current = setTimeout(() => setOpen(false), 160); };
  const fk = fileKindStyle(downloadName);
  const tipUser = (user?.id && dirById[user.id]) || user;
  const canDl = !!downloadUrl;

  const canPreview = canDl && !!previewKind(downloadName);
  const download = async (e) => {
    e.stopPropagation();
    if (!canDl) return;
    if (canPreview) { setOpen(false); setPreview(true); return; }  // spec iter306 — aperçu intégré PDF / image
    try {
      const r = await api.get(downloadUrl, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      const cd = r.headers["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(cd);
      link.href = url; link.download = m ? decodeURIComponent(m[1]) : "fichier";
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error("Téléchargement impossible", { description: err.response?.data?.detail || "" });
    }
  };

  return (
    <span className="relative inline-flex" onMouseEnter={show} onMouseLeave={hide}>
      {children}
      {open && (
        <div style={{ position: "fixed", left: pos.x, top: pos.y, transform: "translateX(-50%)", zIndex: 9999, paddingTop: 8 }}
          onMouseEnter={cancelClose} onMouseLeave={hide} data-testid={`${testId}-tip-bridge`}>
        <div className="flex items-center gap-3 rounded-lg"
          style={events && events.length > 0
            ? { background: "#1a1a1e", border: "0.5px solid #2e2e34", borderRadius: 8, padding: "11px 13px", maxWidth: 380, boxShadow: "0 10px 24px rgba(0,0,0,0.55)" }
            : { background: "#161616", border: "1px solid #2A2A2A", padding: "8px 12px", boxShadow: "0 10px 24px rgba(0,0,0,0.55)", maxWidth: 420 }}
          onClick={(e) => e.stopPropagation()} data-testid={`${testId}-tip`}>
          {events && events.length > 0 ? (
            <SessionEvents events={events} more={more} usersById={usersById} seed={seed} testId={testId} />
          ) : (<>
          {tipUser ? <UserAvatar user={tipUser} size={26} showPopover={false} className="shrink-0" />
            : <span className="inline-flex items-center justify-center rounded-full font-semibold shrink-0" style={{ width: 26, height: 26, background: "#3B3B3B", color: "#9a9aa2", fontSize: 11 }}>—</span>}
          <span className="flex flex-col leading-tight text-left whitespace-nowrap">
            <span className="text-[12px] font-semibold text-white">{label}</span>
            <span className="text-[10.5px] tabular-nums" style={{ color: "#8a8a8f" }}>{fmt(iso)}</span>
          </span>
          </>)}
          {!(events && events.length > 0) && (<>
          <span aria-hidden="true" style={{ width: 1, height: 26, background: "#2a2a2e", flexShrink: 0 }} />
          <span className="flex items-center gap-1.5">
            <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(false); setQuick(true); }}
              className="inline-flex items-center justify-center rounded-md transition-opacity hover:opacity-80"
              style={{ width: 24, height: 24, background: "#1e2a3a", color: "#7bb3e8" }}
              title="Aperçu rapide" data-testid={`${testId}-open`}><Eye size={13} /></button>
            <button type="button" onClick={download} disabled={!canDl}
              className="inline-flex items-center justify-center rounded-md transition-opacity hover:opacity-80 disabled:opacity-35 disabled:cursor-not-allowed"
              style={{ width: 24, height: 24, background: canDl ? fk.bg : "#1a1a1e", color: canDl ? fk.fg : "#8a8a8f" }}
              title={!canDl ? "Aucun fichier" : canPreview ? `Aperçu (${fk.label}${downloadName ? " · " + downloadName : ""})` : `Télécharger (${fk.label}${downloadName ? " · " + downloadName : ""})`}
              data-testid={`${testId}-download`}>{canPreview ? <Maximize2 size={13} /> : <Download size={13} />}</button>
          </span>
          </>)}
        </div>
        </div>
      )}
      {preview && (
        <FilePreviewDialog open={preview} onOpenChange={setPreview} url={downloadUrl} filename={downloadName} />
      )}
      {quick && quickView && <QuickViewDialog kind={quickView.kind} id={quickView.id} onClose={() => setQuick(false)} />}
    </span>
  );
}

const DOC_STATUS_FR = { en_cours: "En cours", terminee: "À vérifier", termine: "À vérifier", cloture: "Clôturé", suspendu: "Suspendu", annule: "Annulé", delai_depasse: "Délai dépassé" };
const stripStep = (l) => { const m = /«\s*([^»]+?)\s*»/.exec(String(l || "")); return m ? m[1] : String(l || ""); };
/** Libellé lisible d'un événement d'historique document (même source que l'onglet Historique). */
export function docEventLabel(e) {
  const step = stripStep(e.field_label);
  switch (e.event_type) {
    case "created": return "Création du document";
    case "step_close": return `Étape validée : ${step || "—"}`;
    case "step_value": return `Valeur d'étape modifiée : ${step || e.field_label || "—"}`;
    case "step_file": return `Fichier d'étape : ${e.after || step}`;
    case "file_add": return `Fichier ajouté : ${e.after || "—"}${step ? ` (${step})` : ""}`;
    case "file_replace": return `Fichier remplacé : ${e.after || "—"}${e.field_label ? ` (${stripStep(e.field_label)})` : ""}`;
    case "file_delete": return `Fichier supprimé : ${e.before || "—"}${step ? ` (${step})` : ""}`;
    case "field_change": return `Champ modifié : ${e.field_label || e.field_key || "—"}`;
    case "status_change": return `Statut : ${DOC_STATUS_FR[e.before] || e.before || "—"} → ${DOC_STATUS_FR[e.after] || e.after || "—"}`;
    case "step_added": return `Étape ajoutée : ${e.field_label || "—"}`;
    case "step_removed": return `Étape retirée : ${e.field_label || "—"}`;
    case "step_removed_manual": return `Étape supprimée : ${e.field_label || "—"}`;
    case "corriger": return "Correction demandée (à refaire)";
    default: return e.note || e.field_label || e.event_type;
  }
}

/** Wrapper Documents. */
export default function ActivityHovercard({ doc, user, usersById, children }) {
  const a = doc.activity;
  if (!a || !doc.activity_at) return children;
  const events = (doc.recent_events || []).map((e) => ({ ...e, label: docEventLabel(e) }));
  return (
    <ActivityTip iso={doc.activity_at} user={user} seed={a.actor_id || doc.id} testId={`doc-activity-${doc.id}`} events={events} more={doc.recent_events_more || 0} usersById={usersById}
      label={a.step_label || (a.kind === "created" ? "Création" : "Modification")}
      quickView={{ kind: "document", id: doc.id }}
      downloadUrl={a.download_path || null}
      downloadName={a.attachment_name}>
      {children}
    </ActivityTip>
  );
}

const TASK_EVENT_LABEL = {
  created: "Création", assignee_added: "Assignation", assignee_status_changed: "Changement de statut",
  attachment_removed: "Pièce jointe retirée", attachment_added: "Pièce jointe ajoutée", checklist_added: "Checklist",
  checklist_reordered: "Checklist réordonnée", closed: "Clôture", completed: "Terminée", deleted: "Suppression",
  field_changed: "Modification", reopened: "Réouverture", restored: "Restauration", to_verify: "À vérifier",
  redo: "À refaire", message: "Message",
};

/** Wrapper Tâches (liste + Kanban). */
export function TaskActivityHovercard({ task, user, usersById, children }) {
  const a = task.activity;
  const iso = a?.at || task.updated_at || task.created_at;
  if (!iso) return children;
  const events = (task.recent_events || []).map((e) => ({ ...e, label: taskEventLabel(e) }));
  return (
    <ActivityTip iso={iso} user={user} seed={a?.actor_id || task.id} testId={`task-activity-${task.id}`} events={events} more={task.recent_events_more || 0} usersById={usersById}
      label={TASK_EVENT_LABEL[a?.type] || a?.type || "Activité"}
      quickView={{ kind: "task", id: task.id }}
      downloadUrl={a?.attachment_id ? `/tasks/${task.id}/attachments/${a.attachment_id}/download` : null}
      downloadName={a?.attachment_name}>
      {children}
    </ActivityTip>
  );
}

/** Wrapper AVI. */
export function AviActivityHovercard({ avi, user, children }) {
  const a = avi.activity;
  const iso = a?.at || avi.updated_at || avi.created_at;
  if (!iso) return children;
  return (
    <ActivityTip iso={iso} user={user} seed={a?.actor_id || avi.id} testId={`avi-activity-${avi.id}`}
      label={a?.label || "Activité"} quickView={{ kind: "avi", id: avi.id }}
      downloadUrl={a?.attachment_id ? `/avi/${avi.id}/attachments/${a.attachment_id}/download` : null}
      downloadName={a?.attachment_name}>
      {children}
    </ActivityTip>
  );
}
